<template>
  <div class="adjust-page">
    <div class="content">
      <van-card
        :desc="formatDesc(productInfo)"
        :price="formatePrice(productInfo)"
        :title="productInfo.productName"
        :thumb="'https://fastly.jsdelivr.net/npm/@vant/assets/ipad.jpeg'"
      >
        <template #price>
          <div class="flex flex-between">
            <span class="price">{{ formatePrice(productInfo) }}</span>
            <span v-if="productInfo.isWeigh" class="weight-badge">称</span>
          </div>
        </template>
        <template #footer>
          <div class="info-row">
            <div class="info-item">
              <span class="info-label">系统库存:</span>
              <span class="info-value">{{ productInfo.stockNum }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">当前盘点数:</span>
              <span class="info-value">{{
                productInfo.currentInventoryNum
              }}</span>
            </div>
          </div>
        </template>
      </van-card>

      <van-form @submit="handleConfirm">
        <div class="form-wrapper">
          <div class="form-item">
            <!-- 只读 + inputmode=none，避免弹出系统软键盘 -->
            <van-field
              ref="adjustField"
              v-model="form.adjustNum"
              type="text"
              inputmode="none"
              readonly
              clickable
              label="盘点数增加"
              placeholder="请输入调整数量"
              :rules="[
                { required: true, message: '请输入调整数量（负数表示减少）' },
              ]"
              @click="openKeyboard"
              @touchstart.native.prevent="openKeyboard"
            />
          </div>
        </div>

        <div class="button-wrapper">
          <van-button
            round
            block
            type="default"
            plain
            hairline
            native-type="button"
            :disabled="submitting"
            @click="handleCancel"
            >取消</van-button
          >
          <van-button
            round
            block
            type="primary"
            native-type="submit"
            :loading="submitting"
            :disabled="submitting"
            >确认</van-button
          >
        </div>
      </van-form>
    </div>

    <van-number-keyboard
      :show="showKeyboard"
      theme="custom"
      :extra-key="keyboardExtraKeys"
      close-button-text="完成"
      safe-area-inset-bottom
      z-index="200"
      :hide-on-click-outside="false"
      @input="onKeyboardInput"
      @delete="onKeyboardDelete"
      @blur="onKeyboardBlur"
    />
  </div>
</template>

<script>
import { NumberKeyboard } from "vant";
import { addProductTakeNum } from "../../../service/api/stock/take/index";

export default {
  name: "InventoryAdjust",
  components: {
    [NumberKeyboard.name]: NumberKeyboard,
  },
  data() {
    return {
      submitting: false,
      showKeyboard: false,
      form: {
        adjustNum: "",
      },
      productInfo: {
        planId: null,
        sheetId: null,
        productId: null,
        productName: "",
        productCode: "",
        externalCode: "",
        stockNum: 0,
        currentInventoryNum: 0,
        isWeigh: false,
        retailPrice: 0,
        unit: "",
      },
    };
  },
  computed: {
    // 始终提供小数点、负号（自定义键盘左下角）
    keyboardExtraKeys() {
      return [".", "-"];
    },
  },
  mounted() {
    const q = this.$route.query || {};
    const productId = q.productId || null;
    const sheetId = q.sheetId || null;
    if (!productId || !sheetId) {
      this.$toast("缺少盘点参数，请返回重试");
      this.$router.go(-1);
      return;
    }

    const currentInventoryNum =
      q.inventoryNum === 0 || q.inventoryNum || q.takeNum === 0 || q.takeNum
        ? q.inventoryNum != null && q.inventoryNum !== ""
          ? q.inventoryNum
          : q.takeNum
        : "";

    this.productInfo = {
      planId: q.planId || null,
      sheetId,
      productId,
      productName: q.productName || "",
      productCode: q.productCode || "",
      externalCode: q.externalCode || "",
      stockNum: q.stockNum || 0,
      currentInventoryNum,
      isWeigh: q.isWeigh === "1" || q.isWeigh === 1 || q.isWeigh === true,
      retailPrice: q.retailPrice || 0,
      unit: q.unit || "",
    };

    // 进入页面自动打开自定义数字键盘
    this.$nextTick(() => {
      this.openKeyboard();
    });
  },
  beforeDestroy() {
    this.showKeyboard = false;
  },
  methods: {
    openKeyboard() {
      if (this.submitting) {
        return;
      }
      this.showKeyboard = true;
    },

    onKeyboardBlur() {
      // 提交中保持关闭；其余允许点「完成」收起，点击输入框可再打开
      if (!this.submitting) {
        this.showKeyboard = false;
      }
    },

    onKeyboardInput(key) {
      if (this.submitting) {
        return;
      }
      const next = String(this.form.adjustNum || "") + String(key);
      this.applyAdjustInput(next);
    },

    onKeyboardDelete() {
      if (this.submitting) {
        return;
      }
      const cur = String(this.form.adjustNum || "");
      this.form.adjustNum = cur.slice(0, -1);
    },

    // 输入过滤：支持负号；小数最多两位（称重/非称重均可）
    applyAdjustInput(value) {
      let processed = value == null ? "" : String(value);
      processed = processed.replace(/[^-\d.]/g, "");

      if (processed.lastIndexOf("-") > 0) {
        processed = "-" + processed.replace(/-/g, "");
      }

      const firstDot = processed.indexOf(".");
      if (firstDot !== -1) {
        // 只保留第一个小数点，小数最多两位
        processed =
          processed.slice(0, firstDot + 1) +
          processed.slice(firstDot + 1).replace(/\./g, "");
        const intPart = processed.slice(0, firstDot + 1);
        const decimalPart = processed.slice(firstDot + 1).slice(0, 2);
        processed = intPart + decimalPart;
      }

      this.form.adjustNum = processed;
    },

    // 兼容旧 @input 入口（当前字段只读，基本不会走到）
    handleInput(value) {
      this.applyAdjustInput(value);
    },

    handleCancel() {
      if (this.submitting) {
        return;
      }
      this.showKeyboard = false;
      const {
        productId,
        productName,
        productCode,
        externalCode,
        stockNum,
        inventoryNum,
        takeNum,
        isWeigh,
        retailPrice,
        unit,
        updatedProductId,
        updatedAdjustNum,
        ...queryObj
      } = this.$route.query;
      this.$router.replace({
        path: "/stock/inventory/goods",
        query: { ...queryObj },
      });
    },

    async handleConfirm() {
      if (this.submitting) {
        return;
      }
      const takeNum = String(this.form.adjustNum || "").trim();
      if (!takeNum || takeNum === "-" || takeNum === "." || takeNum === "-.") {
        this.$toast("请输入有效的调整数量");
        this.openKeyboard();
        return;
      }

      this.submitting = true;
      this.showKeyboard = false;
      let success = false;
      try {
        const res = await addProductTakeNum({
          sheetId: this.productInfo.sheetId,
          productId: this.productInfo.productId,
          takeNum,
        });
        if (res.code !== 200) {
          this.$toast(res.msg || "修改盘点数失败");
          return;
        }
        success = true;
        this.$toast("修改成功");
        const {
          productId,
          productName,
          productCode,
          externalCode,
          stockNum,
          inventoryNum,
          takeNum: _takeNum,
          isWeigh,
          retailPrice,
          unit,
          updatedProductId,
          updatedAdjustNum,
          ...queryObj
        } = this.$route.query;
        this.$router.replace({
          path: "/stock/inventory/goods",
          query: {
            ...queryObj,
            updatedProductId: this.productInfo.productId,
            updatedAdjustNum: takeNum,
          },
        });
      } catch (e) {
        console.warn("修改盘点数失败", e);
        this.$toast(
          (e && e.msg) ||
            (e && e.response && e.response.data && e.response.data.msg) ||
            "修改盘点数失败，请重试",
        );
      } finally {
        this.submitting = false;
        // 失败时再开键盘；成功已跳转，无需打开
        if (!success) {
          this.openKeyboard();
        }
      }
    },
    formatDesc(item) {
      return `商品条码：${item.externalCode || "--"}`;
    },
    formatePrice(item) {
      return "￥" + item.retailPrice + (item.unit ? `/${item.unit}` : "");
    },
  },
};
</script>

<style scoped>
::v-deep .van-card__price {
  width: 100%;
}
.adjust-page {
  min-height: 100vh;
  background: #f7f8fa;
}

.content {
  /* 预留底部自定义键盘高度，避免确认按钮被挡住 */
  padding: 16px 16px 280px;
}

.product-title {
  display: inline-block;
  vertical-align: middle;
}

.weight-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 6px;
  background: #ff6b00;
  color: #fff;
  font-size: 12px;
  border-radius: 50%;
  vertical-align: middle;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
}

.info-item {
  flex: 1;
}

.info-label {
  color: #969799;
  font-size: 12px;
}

.info-value {
  color: #323233;
  font-size: 14px;
  margin-left: 4px;
}

.form-wrapper {
  margin-top: 16px;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
}

.form-item {
  margin-bottom: 8px;
}

.adjust-hint {
  font-size: 12px;
  color: #999;
  padding-left: 10px;
}

.button-wrapper {
  margin-top: 28px;
  display: flex;
  gap: 10px;
  padding: 0 4px;
}

.button-wrapper .van-button {
  flex: 1;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
}
</style>
